import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "@/components/Layout/PageShell";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { KeyRound, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("verifying"); // verifying | valid | invalid | success | submitting
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.get(`/auth/reset-token/verify?token=${encodeURIComponent(token)}`)
      .then((r) => setStatus(r.data.ok ? "valid" : "invalid"))
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setStatus("submitting");
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setStatus("success");
      toast.success("Password updated. Redirecting to login…");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password.");
      setStatus("invalid");
    }
  };

  return (
    <PageShell>
      <Seo title="Choose a new password" path="/reset-password" />
      <section className="container-spark py-16 max-w-md">
        <div className="card-spark p-10">
          {status === "verifying" && (
            <div className="text-center space-y-3" data-testid="reset-verifying">
              <Loader2 className="mx-auto animate-spin text-spark-purple" size={36} />
              <p className="text-sm text-muted-foreground">Verifying your reset link…</p>
            </div>
          )}
          {status === "invalid" && (
            <div className="text-center space-y-3" data-testid="reset-invalid">
              <XCircle className="mx-auto text-red-500" size={48} />
              <h1 className="text-2xl font-bold">Link expired or invalid</h1>
              <p className="text-sm text-muted-foreground">
                This reset link is no longer valid. Request a new one and try again.
              </p>
              <Link to="/forgot-password" className="btn-primary inline-flex">
                Request a new link
              </Link>
            </div>
          )}
          {(status === "valid" || status === "submitting") && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <KeyRound size={22} className="text-spark-purple" />
                <h1 className="text-2xl font-bold">Choose a new password</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Pick something at least 8 characters. You&apos;ll be logged in fresh after this.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <label className="block">
                  <span className="text-xs font-medium">New password</span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    className="input-spark mt-1"
                    data-testid="reset-new-password"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium">Confirm password</span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    className="input-spark mt-1"
                    data-testid="reset-confirm-password"
                  />
                </label>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="btn-primary w-full"
                  data-testid="reset-submit"
                >
                  {status === "submitting"
                    ? <Loader2 className="animate-spin" size={16} />
                    : "Update password"}
                </button>
              </form>
            </>
          )}
          {status === "success" && (
            <div className="text-center space-y-3" data-testid="reset-success">
              <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
              <h1 className="text-2xl font-bold">Password updated</h1>
              <p className="text-sm text-muted-foreground">Redirecting you to log in…</p>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
